<template>
	<div class="jw-modal-new">
		<header class="jw-modal-new__header" v-if="hasHeader">
			<div class="jw-modal-new-toolbar" :class="{ 'jw-toolbar-small': !hasLargeToolbar }">
				<div
					class="jw-modal-new__header--icons jw-modal-new__header--icons--left"
					:style="!hasBack ? { visibility: 'hidden' } : {}"
				>
					<button @click="$emit('back')">
						<FontAwesomeIcon slot="icon-only" :icon="faAngleLeft" />
					</button>
				</div>

				<div v-show="title || $slots.title" class="jw-modal-new-title jw-modal-new__header--title">
					<slot name="title">
						<div class="jw-modal-title">
							{{ title }}
						</div>
					</slot>
				</div>

				<div v-show="!title && !$slots.title && showDefaultTitle" class="jw-modal-new-title">
					<div class="jw-modal-new__header--title jw-modal__header--hero-title">
						<img :src="`${ASSETS_DIRECTORY}/img/logo/JustWatch-logo.png`" alt="JustWatch" />
					</div>
				</div>

				<div v-if="closable" class="jw-modal-new__header--icons">
					<button class="new-design-modal__close" @click="handleDismiss">
						<FontAwesomeIcon slot="icon-only" :icon="faTimes" />
					</button>
				</div>
			</div>

			<div v-if="!title && !$slots.title && showDefaultTitle" class="hero-header__hero-toolbar">
				<p class="hero-toolbar--description">
					{{ $t('WEBAPP_GLOBALHOME_CLAIM_DESCRIPTION', { providerlist: descriptionProviderList }) }}
				</p>
			</div>
		</header>

		<section class="jw-modal-new__body">
			<slot />
		</section>

		<div v-if="$slots.right">
			<slot name="right"></slot>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';

import { faTimes, faAngleLeft } from '@fortawesome/free-solid-svg-icons';

import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { afterNextPaint } from '@/helpers/async-helper';

const props = withDefaults(
	defineProps<{
		title?: string;
		closable?: boolean;
		hasBack?: boolean;
		hasHeader?: boolean;
		showDefaultTitle?: boolean;
		hasLargeToolbar?: boolean;
		onWillDismiss?: () => Promise<void>;
	}>(),
	{
		title: '',
		closable: true,
		hasHeader: true,
		showDefaultTitle: true,
		hasLargeToolbar: true,
		onWillDismiss: () => Promise.resolve(),
	}
);

const emit = defineEmits(['back', 'closed', 'dismiss']);

const ASSETS_DIRECTORY = `/${ASSETS_DIR}`;

const { providers } = useConstantStore();

onMounted(async () => {
	await props?.onWillDismiss();
	emit('closed');
});

const descriptionProviderList = computed(() => {
	return providers()
		.slice(0, 3)
		.map(({ clearName }: any) => ` ${clearName}`);
});

async function handleDismiss() {
	// IPN case: trailer modal is one of the heaviest UI pieces we have.
	// that's why we split the click and the heavy 'dismiss' into two tasks
	await afterNextPaint();

	emit('dismiss');
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins';

.jw-modal-new {
	display: flex;
	flex-direction: column;
	$toolbar-height: 86px;
	$footer-height: 49px;

	text-align: center;

	&--is-mobile {
		text-align: initial;

		.jw-modal-new__header--subtitle {
			padding-inline-start: 20px;
			// +20 to dodge close icon
			padding-inline-end: 40px;
		}
	}

	header {
		padding: 16px;
	}

	section {
		flex-grow: 1;
	}

	.jw-modal-new-toolbar {
		display: flex;
		justify-content: space-between;
		--min-height: #{$toolbar-height};
		--background: var(--jw-background-color) !important;

		&.jw-toolbar-small {
			--min-height: 56px;
		}

		.jw-modal-new__header--icons {
			button {
				padding: 12px;
				width: 48px;
				height: 48px;
				color: white;
				background-color: transparent;
				border-radius: 100rem;
				border-color: transparent;
				cursor: pointer;

				&:hover {
					background-color: rgba(213, 213, 213, 0.05);
				}
			}
		}
	}

	.jw-modal-new__body {
		height: 100%;
		--background: var(--jw-background-color) !important;
		overflow-y: auto;
	}

	.jw-modal__header--hero-title {
		transition-property: none;
		margin: 45px auto 0;
		width: 180px;
	}

	.hero-toolbar--description {
		padding: 0 48px;
		color: $jw-color-mediumgray--light;
		font-size: 16px;
		letter-spacing: 0.3px;
		line-height: 26px;
	}

	&__header {
		background: none;
		&--title {
			font-size: 24px;
			font-weight: 900;
			min-width: 0;

			.jw-modal-title {
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
			}
		}
		&--subtitle {
			font-size: 14px;
			color: var(--jw-color-secondary-tint);
		}
		&--icons {
			width: 50px;
			@include inset-inline('end', 0);
			background: none;

			&--left {
				@include inset-inline('start', 20px);
				.svg-inline--fa {
					font-size: 17px;
					font-weight: bold;
				}
			}
		}
		&:after {
			background: none;
		}
	}
}
</style>
